import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'

export function getAgentHubExperienceType(experience: App.ExperienceOffer): App.ProductType {
  switch (experience.productType) {
    case 'transfer':
      return 'transfer'
    default:
      return 'experience'
  }
}

export function getAgentHubCommissionItemKey(item: App.Checkout.AnyItem | App.Checkout.AnyItemBreakdownView): string {
  const transactionKey = item.transactionKey

  if ('experienceId' in item && item.itemType === 'experience') {
    return transactionKey + '-' + item.experienceId
  }

  if (item.itemType === 'tourV2Experience' && 'purchasableOption' in item) {
    return item.transactionKey + '-' + item.purchasableOption.fkTourId
  }

  if ('offerId' in item) {
    return transactionKey + '-' + item.offerId
  }

  if (isFlightItem(item as App.Checkout.AnyItem)) {
    return transactionKey + '-' + (item as App.Checkout.FlightItem).flights[0]?.journeyId
  }
  return transactionKey!
}

const TIER_ORDER: Array<App.AgentHubTier> = ['tier1', 'tier2', 'tier3']

export function isAgentTierGreaterOrEqualTo(agentTier: App.AgentHubTier, requiredTier: App.AgentHubTier): boolean {
  return TIER_ORDER.indexOf(agentTier) >= TIER_ORDER.indexOf(requiredTier)
}

export const isAgencyIdentifierRequired = (currentRegionCode: string) => {
  const nonRequiredRegions = ['GB', 'US']
  return !nonRequiredRegions.includes(currentRegionCode)
}

export const mapTourVariationToAgentHubDateRangePayload = (variation: App.Tours.TourV2OfferVariation): Array<{ id: string; startDate: string; endDate: string }> => {
  return variation.departures.map(departure => ({
    id: departure.id,
    startDate: departure.startDate,
    endDate: departure.endDate,
  }))
}

export const mapCruiseDepartureToAgentHubDateRangePayload = (departure: App.CruiseDepartureSummary | App.CruiseDepartureDetails): { id: string; startDate: string; endDate: string } => {
  return {
    id: departure.id,
    startDate: departure.departureDate ?? '',
    endDate: departure.arrivalDate ?? '',
  }
}

export const mapCruiseDepartureToAgentHubLocationPayload = (offer: App.CruiseOffer): Array<string> => {
  const departureLocation = offer.departurePort.split(',').map(port => port.trim())
  const arrivalLocation = offer.returnPort.split(',').map(port => port.trim())
  return [...departureLocation, ...arrivalLocation]
}

export const getOfferPriceAfterCommission = (price: number, offer: App.AgentHubOfferCommission | App.AgentHubOfferCommission['packages'][number] | undefined) => {
  if (!agentHubOrSubBrandConfig.isEnabled || !offer) return price
  return Number((price * (1 - offer.commissionPercentage / 100) || 0).toFixed(2))
}

export const getCalculationRequestAfterCommissionIfAgentHubEnabled = (calculationRequest: App.LoyaltyPointsEarnCalculationRequest, agentHubCommissionMap?: { [offerId: string]: App.AgentHubOfferCommission | undefined }) => {
  if (!agentHubOrSubBrandConfig.isEnabled || !agentHubCommissionMap || !calculationRequest.offerId || !agentHubCommissionMap[calculationRequest.offerId]) {
    return calculationRequest
  }

  const offerCommission = agentHubCommissionMap[calculationRequest.offerId || '']
  const commissionForPackageId = calculationRequest.packageId ? offerCommission?.packages.find((p) => p.commissionPercentage > 0 && p.packageId === calculationRequest.packageId) : undefined
  const commissionToApply = commissionForPackageId ?? offerCommission
  const memberPrice = getOfferPriceAfterCommission(calculationRequest.memberPrice ?? calculationRequest.price, commissionToApply)
  const price = getOfferPriceAfterCommission(calculationRequest.price, commissionToApply)
  return {
    ...calculationRequest,
    price,
    memberPrice,
  }
}

export const calculateGrossCommissionPercentage = (itemsCommission: Record<string, App.AgentHubItemCommissionDetails> | undefined) => {
  if (!itemsCommission) return 0

  const { totalCommission, totalPrice } = Object.values(itemsCommission).reduce(
    (totals, item) => ({
      totalCommission: totals.totalCommission + item.calculatedCommissionPercentage * (item.itemPrice ?? 0),
      totalPrice: totals.totalPrice + (item.itemPrice ?? 0),
    }),
    { totalCommission: 0, totalPrice: 0 },
  )

  if (totalPrice === 0) return 0
  return totalCommission / totalPrice
}

export const getCruiseMargin = (itemView: App.Checkout.CruiseAccommodationItemView): number => {
  const pricing = itemView.cabinSelection?.pricing
  if (!pricing) return 0
  if (pricing.commission !== undefined) return pricing.commission.amount
  if (!pricing.netFare) return 0
  return pricing.total.amount - pricing.netFare.amount
}
